import { computed, inject, Injectable } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { Observable, of, tap } from 'rxjs';
import { AuthStore } from '@domains/auth/auth.store';
import { RoleApi } from './role.api';
import { IRoleDetail, IRoleSaveRequest, PermissionMap } from './role.model';

@Injectable({ providedIn: 'root' })
export class RoleStore {
  private readonly api = inject(RoleApi);
  private readonly authStore = inject(AuthStore);

  private readonly _roles = rxResource({
    params: () => ({ limit: 100 }),
    stream: ({ params }) => this.api.getRoles(params),
  });

  // Rights of the logged-in user, keyed by the role id from the session
  private readonly _currentUserRole = rxResource({
    params: () => this.authStore.currentUser()?.role.id,
    stream: ({ params }) => this.api.getRole(params),
  });

  readonly roles = computed(() => this._roles.value()?.results ?? []);
  readonly activeRoles = computed(() => this.roles().filter((role) => role.is_active));
  readonly isLoading = computed(() => this._roles.isLoading());
  readonly error = computed(() => this._roles.error());

  readonly currentUserPermissions = computed<PermissionMap>(() =>
    parsePermissionMap(this._currentUserRole.value()?.right_management),
  );
  readonly currentUserPermissionsLoading = computed(() => this._currentUserRole.isLoading());

  getRole(id: number): Observable<IRoleDetail> {
    return this.api.getRole(id);
  }

  // TODO: backend endpoint for "users this role applies to" does not exist yet —
  // replace this stub once the API is available.
  getRoleUsers(_roleId: number): Observable<string[]> {
    return of([]);
  }

  createRole(input: IRoleSaveRequest): Observable<IRoleDetail> {
    return this.api.createRole(input).pipe(tap(() => this._roles.reload()));
  }

  updateRole(id: number, input: IRoleSaveRequest): Observable<IRoleDetail> {
    return this.api.updateRole(id, input).pipe(
      tap(() => {
        this._roles.reload();
        // the edited role may be the current user's own role
        if (id === this.authStore.currentUser()?.role.id) {
          this._currentUserRole.reload();
        }
      }),
    );
  }

  deleteRole(id: number): Observable<void> {
    return this.api.deleteRole(id).pipe(tap(() => this._roles.reload()));
  }
}

function parsePermissionMap(json: string | undefined): PermissionMap {
  if (!json) return {};
  try {
    const parsed: unknown = JSON.parse(json);
    if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as PermissionMap;
    }
  } catch {
    // malformed permission blob — treat as empty
  }
  return {};
}
